import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Button } from '@/components';
import { getHandoverById } from '@/services/api/handovers';
import { extractApiErrorMessage } from '@/utils';
import { showErrorToast } from '@/components/toast/globalToast';
import { useListReturnPath } from '@/hooks/useUrlListState';

const PreReturnHandoverDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const backTo = useListReturnPath('/handovers');
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);

    try {
      if (id.startsWith('dummy-')) {
        // purely mockup data for slicing
        setTimeout(() => {
          setData({
            platNomor: 'B 1234 ABC',
            oemName: 'Electrum',
            modelName: 'H3',
            vehicleBusinessUnit: 'QUICK_COMMERCE',
            vehicleHubName: 'Hub Jaksel',
            driverCode: 'MTR1234',
            driverName: 'Naipin',
            clientName: 'Sayurbox',
            hubName: 'Kramat Hub',
            resignType: 'Self Resign',
            requestDate: '2026-01-26T15:00:00Z',
          });
          setIsLoading(false);
        }, 500);
        return;
      }

      const response = await getHandoverById(id);
      setData(response);
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(
          error,
          'Failed to load pre-return handover detail'
        )
      );
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const InfoBlock = ({
    label,
    value,
  }: {
    label: string;
    value: React.ReactNode;
  }) => (
    <div className="flex flex-col mb-4">
      <span className="text-xs text-gray-400 font-medium mb-1">{label}</span>
      <span className="text-sm font-medium text-gray-800">{value || '-'}</span>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5e2aac] mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading details...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Not Found</h1>
          <p className="text-gray-600 mb-6">
            The handover case could not be found.
          </p>
          <Button onClick={() => navigate(backTo)} variant="primary">
            &larr; Go Back
          </Button>
        </div>
      </div>
    );
  }

  const titleStr = `${data.resignType || 'Resign'} - ${data.platNomor}`;
  const requestDateStr = data.requestDate
    ? dayjs(data.requestDate).format('DD MMMM YYYY, HH.mm')
    : '-';

  return (
    <div className="max-w-full mx-auto bg-white px-6 py-5 min-h-screen">
      <div className="mx-auto">
        {/* Header Section */}
        <div className="mb-4 flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <button
              onClick={() => navigate(backTo)}
              className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              aria-label="Back"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {titleStr}
              </h1>
              <p className="mt-0.5 text-xs text-gray-500">
                Requested on {requestDateStr}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center sm:justify-end gap-2 mt-0.5 w-full md:w-auto shrink-0">
            <Button
              variant="primary"
              className="bg-purple-700 text-white hover:bg-purple-800 font-medium"
              onClick={() => {
                // Implement Make Decision logic here
              }}
            >
              Make Decision
            </Button>
          </div>
        </div>

        {/* Warning Banner */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <i className="ri-error-warning-line text-yellow-600 text-xl mt-0.5"></i>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              Pending Resignation Proposal
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed">
              This Mitra has submitted a resignation request, but their status
              remains Active/Ready to Work. Please meet the rider to inspect the
              vehicle physically, then finalize the return by clicking "Approve
              & Finalize Return" to complete the handover and update their
              status to Resigned.
            </p>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex flex-col gap-6 lg:flex-row items-start">
          {/* Left Panel: Vehicle Details */}
          <div className="w-full lg:w-7/12 border border-gray-200 rounded-xl overflow-hidden bg-white">
            <div className="px-5 py-4 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">
                Vehicle Details
              </h2>
            </div>

            {/* Vehicle Group */}
            <div className="bg-gray-50 px-5 py-2">
              <span className="text-xs font-medium text-gray-400 uppercase">
                VEHICLE
              </span>
            </div>
            <div className="p-5">
              <InfoBlock label="Plate Number" value={data.platNomor} />
              <div className="grid grid-cols-2 gap-4">
                <InfoBlock label="OEM" value={data.oemName} />
                <InfoBlock label="Models" value={data.modelName} />
              </div>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <InfoBlock
                  label="Business Unit"
                  value={
                    data.vehicleBusinessUnit === 'QUICK_COMMERCE'
                      ? 'Quick Commerce'
                      : data.vehicleBusinessUnit
                  }
                />
                <InfoBlock label="Hub Location" value={data.vehicleHubName} />
              </div>
            </div>

            {/* Mitra Group */}
            <div className="bg-gray-50 px-5 py-2">
              <span className="text-xs font-medium text-gray-400 uppercase ">
                MITRA
              </span>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-4">
                <InfoBlock label="MTRCode" value={data.driverCode} />
                <InfoBlock label="Mitra Name" value={data.driverName} />
              </div>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <InfoBlock label="Client" value={data.clientName} />
                <InfoBlock label="Pitstop" value={data.hubName} />
              </div>
            </div>
          </div>

          {/* Right Panel: Resign Info */}
          <div className="w-full lg:w-5/12 border border-gray-200 rounded-xl overflow-hidden bg-white">
            <div className="px-5 py-4 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">
                Resign Info
              </h2>
            </div>

            <div className="bg-gray-50 px-5 py-2">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
                RESIGN INFO
              </span>
            </div>
            <div className="p-5">
              <span className="text-xs text-gray-400 font-medium mb-2 block">
                Reason
              </span>
              <p className="text-sm text-gray-800 leading-relaxed font-medium">
                Other - I have recently relocated to a different city and will
                no longer be able to continue this partnership. I'd like to
                thank everyone for the opportunity.
                <br />
                Other - I have recently relocated to a different city and will
                no longer be able to continue this partnership.
                <br />
                Other - I have recently relocated to a different city and will
                no longer be able to continue this partnership.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreReturnHandoverDetailPage;
